import React, { useContext, useEffect, useRef, useState } from "react";
import {  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import carBrandContext from "../../app/providers/car-brands/carBrandContext";
import CloseIcon from '@mui/icons-material/Close';
import MunHuoltoCar from '../../assets/images/Munhuolto_ikoni_auto.svg';
import { useTranslation } from "react-i18next";
import { useWindowSize } from "../hooks/useWindowSize";
import { selectedVehicleStyle } from "../styles/alertStyles";
import { useDispatch, useSelector } from "react-redux";
import { altChooseButtonStyleTurquoise, altCloseButtonStyleDark, chooseButtonStyleDark, chooseButtonTurquoiseStyle } from "../styles/buttonStyles";
import RegisterPlate from "./RegisterPlate";
import { dialogStyle } from "../styles/dialogStyles";
import { setDraft } from "../../app/store/reservationSlice";
import NumberPlate from "./NumberPlate";

export default function SelectedVehicle({canEdit}) {

    const { t } = useTranslation();

    const draft = useSelector((state) => state.reservation.draft);
    const dispatch = useDispatch();

    const { state: carBrandState, clearBrand } = useContext(carBrandContext);
    const { isDesktop, isTablet, isMobile } = useWindowSize();

    const [ open, setOpen ] = useState(false);
    const registerPlateRef = useRef(null);
    const [plate, setPlate] = useState("")

    useEffect(() => {
        if (draft.car) {
            setOpen(false);
            if(draft.car.plate){
                setPlate(draft.car.plate)
            }
        }
        else{
            setPlate("")
        }
    }, [draft.car]);

    const handleClose = () => {
        setOpen(false);
    }

    const handleApply = () => {
        // Trigger search if no car found yet, otherwise just close
        if (!draft.car && registerPlateRef.current) {
            registerPlateRef.current.search();
        } else if (draft.car) {
            setOpen(false);
        }
    }

    const handleClearVehicle = () => {
        clearBrand();
        dispatch(setDraft({ car: null }));
    }

    const AddVehicleByRegPlate = () => {
        return (
            <NumberPlate plate={plate} handleClick={() => setOpen(true)} canEdit={canEdit} />
        )
    }

    return (
        <>
            <Box 
                sx={{
                    ...selectedVehicleStyle,
                    backgroundColor: 'rgb(214, 252, 255)',
                    color: '#003C59',
                    padding: '12px 16px',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    justifyContent: isMobile ? 'flex-start' : 'space-between',
                    '& .MuiAlert-icon': {
                        color: '#003C59'
                    },
                    '& .MuiAlertTitle-root': {
                        color: '#003C59'
                    },
                    '& .MuiTypography-root': {
                        color: '#003C59'
                    }
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={isMobile ? 2 : 1} sx={{ width: '100%' }}>
                    <img src={MunHuoltoCar} alt="Car" style={{ width: '32px', height: '32px', filter: 'brightness(0) saturate(100%) invert(15%) sepia(77%) saturate(1679%) hue-rotate(175deg) brightness(95%) contrast(101%)' }} />
                    <Box sx={{ display: 'flex', flexDirection: isMobile?'column':'row', gap: isMobile ? 0.5 : 0, alignItems: isMobile ? 'flex-start' : 'center', justifyContent: isMobile ? 'flex-start' : 'space-between', width: '100%' }}>
                        <Box>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : "0.875rem" }}>
                                {draft.car ? 
                                    `${t('selected_vehicle.chosenVehicle')} ${draft.car.basicinfo?.basicinfo?.Make || ''}` :
                                    carBrandState.brand?.name ? 
                                        `${t('selected_vehicle.chosenVehicle')} ${carBrandState.brand.name}` :
                                        t('selected_vehicle.noChosenVehicle')
                                }
                            </Typography>
                            <Typography variant="body2" sx={{ margin: 0, fontSize: isMobile ? '0.75rem' : "0.875rem" }}>
                                {draft.car ? 
                                    draft.car.basicinfo?.basicinfo?.GenericName || '' :
                                    carBrandState.brand?.name ? 
                                        t('selected_vehicle.infoPlateNumber') :
                                        t('selected_vehicle.chooseVehicle')
                                }
                            </Typography>
                            {draft.car && draft.mileage && (
                                <Typography variant="body2" sx={{ margin: 0, fontSize: isMobile ? '0.75rem' : "0.875rem" }}>
                                    {`${t('registerPlate.mileage')} : ${draft.mileage}Km`}
                                </Typography>
                            )}
                        </Box>
                        <Box>
                            <AddVehicleByRegPlate />
                        </Box>
                    </Box>
                </Stack>
            </Box>
            <Dialog open={open} onClose={handleClose}
                sx={{...dialogStyle}}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: 1 }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {t("registerPlate.dialogTitle")}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', marginTop: 1 }}>
                            {t("registerPlate.dialogDescription")}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} sx={{ padding: 0, marginTop: '-4px' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <RegisterPlate ref={registerPlateRef} hideSearchButton={true} />
                </DialogContent>
                <DialogActions 
                    sx={{ 
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: '#003C59',
                        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                        padding: '16px 24px',
                        gap: 2,
                        zIndex: 1
                    }}
                >
                    <Button 
                        onClick={handleClose}
                        variant="outlined"
                        sx={{...altCloseButtonStyleDark, flex: 1}}
                    >
                        {t("common.close")}
                    </Button>
                    <Button 
                        onClick={handleApply}
                        variant="contained"
                        sx={{...altChooseButtonStyleTurquoise, flex: 1 }}
                        disabled={false}
                    >
                        {isMobile ? t("common.apply") : t("registerPlate.searchButton")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
